export type PregnancyPhase =
  't1' | 't2' | 't3' | 'postpartum-standard' | 'postpartum-csection'

export interface PregnancyPromptParams {
  phase:     PregnancyPhase
  injuries?: string
  equipment: 'bodyweight' | 'gym'
}

const PHASE_LABELS: Record<PregnancyPhase, string> = {
  't1':                  '1st Trimester',
  't2':                  '2nd Trimester',
  't3':                  '3rd Trimester',
  'postpartum-standard': 'Postpartum – Standard Delivery',
  'postpartum-csection': 'Postpartum – C-Section',
}

// ---------------------------------------------------------------------------
// Guide table constants — sourced from USAF Pregnancy & Postpartum
// Performance Training Guide
// ---------------------------------------------------------------------------

const GUIDE_T1 = `
FIRST TRIMESTER PROGRAM — 12 weeks total (3 rotating workouts)

IMPORTANT NOTES:
- After 6 weeks of pregnancy, relaxin loosens joints/ligaments. Practice caution with stretching and mobility.
- "Variation" means choose an appropriate exercise from that movement pattern (e.g. Squat Variation = Goblet Squat, Air Squat, Split Squat, etc.)
- Complete all exercises in a block as a circuit before repeating the block. Exercises are timed (seconds), not counted reps.

WARM UP (perform before every workout):
Cardio | 5 minutes
Single Leg Balance | 30 seconds each leg
3 Second Box Breathing (3 in / 3 hold / 3 out) | 10 reps
Plank | 30 seconds
Glute Bridge | 15 reps
Lunge in Place | 5 each leg
World's Greatest Stretch | 5 reps
Push Up | 10 reps

COOL DOWN (perform after every workout):
Walking | 5 minutes
Quad Pull | 5 each leg
Straight Leg March | 5 each leg
Lunge w/ Overhead Reach | 5 each leg
Hamstring Scoop | 5 each leg
Lateral Lunge | 5 each leg
Hip Cradle (if possible) | 5 each leg
Child Pose | 30 seconds

--- WEEKS 1–6 ---

WORKOUT 1 (Weeks 1–6):
Movement | Weeks 1–2 | Weeks 3–4 | Weeks 5–6
Squat Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Overhead Push/Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Carry Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal Push Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal/Vertical Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Hinge Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Plank Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Birth Squat | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Aerobic Conditioning: Run, Row, or Bike — 60 sec on / 120 sec off for 15 minutes

WORKOUT 2 (Weeks 1–6):
Movement | Weeks 1–2 | Weeks 3–4 | Weeks 5–6
Hinge Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Overhead Push/Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Carry Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal Push Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal/Vertical Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Squat Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Plank Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Birth Squat | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Aerobic Conditioning: Jog 30 minutes

WORKOUT 3 (Weeks 1–6):
Movement | Weeks 1–2 | Weeks 3–4 | Weeks 5–6
Squat Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Overhead Push/Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Carry Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal Push Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal/Vertical Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Hinge Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Plank Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Birth Squat | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Aerobic Conditioning: Jog 30 minutes

--- WEEKS 7–12 ---

WORKOUT 1 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
Hinge Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Overhead Push/Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Carry Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal Push Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal/Vertical Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Squat Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Plank Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Birth Squat | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Aerobic Conditioning: Jog 30 minutes

WORKOUT 2 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
Squat Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Overhead Push/Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Carry Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal Push Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal/Vertical Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Hinge Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Plank Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Birth Squat | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Aerobic Conditioning: Jog 3 minutes on / 2 minutes off x8

WORKOUT 3 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
Squat Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Overhead Push/Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Carry Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal Push Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Horizontal/Vertical Pull Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Hinge Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Plank Variation | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Birth Squat | 3 x 30 sec | 4 x 30 sec | 5 x 30 sec
Aerobic Conditioning: Jog 15 minutes
`

const GUIDE_T2 = `
SECOND TRIMESTER PROGRAM — 12 weeks total (3 rotating workouts)

IMPORTANT NOTES:
- Avoid prone position exercises (belly-down). By 20 weeks the uterus is visible and prone position is contraindicated.
- "Variation" means choose an appropriate exercise from that movement pattern.
- Weeks 5–6 and 11–12 intentionally reduce sets (deload weeks) — follow the guide exactly.
- Complete all exercises in a block as a circuit before repeating. Exercises are timed (seconds).

WARM UP (perform before every workout):
Foam Roll (needed areas) | 5 minutes
Lateral Band Walks | 10 yards each leg
In Place Band March | 10 steps each leg
Split Squat | 10 each leg
Lateral Split Squat | 10 each leg
Curtsy Squat | 10 each leg
Band Pull Apart | 10 reps
Wall Slide | 10 reps

COOL DOWN — Repeat 2x:
Pelvic Tilts | 10 reps
Door Frame Lat Stretch | 30 seconds each arm
Door Frame Chest Stretch | 30 seconds each arm
Kneeling Hip Flexor (stand if more comfortable) | 30 seconds each leg

--- WEEKS 1–6 ---

WORKOUT 1 (Weeks 1–6):
Movement | Weeks 1–2 | Weeks 3–4 | Weeks 5–6
Squat Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Overhead Push/Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Carry Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal Push Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal/Vertical Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Hinge Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Plank Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Birth Squat | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Aerobic Conditioning: Continuous Step Ups — 4 minutes on / 1 minute off x5

WORKOUT 2 (Weeks 1–6):
Movement | Weeks 1–2 | Weeks 3–4 | Weeks 5–6
Hinge Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Overhead Push/Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Carry Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal Push Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal/Vertical Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Squat Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Plank Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Birth Squat | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Aerobic Conditioning: Jog/Walk 20 minutes

WORKOUT 3 (Weeks 1–6):
Movement | Weeks 1–2 | Weeks 3–4 | Weeks 5–6
Squat Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Overhead Push/Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Carry Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal Push Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal/Vertical Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Hinge Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Plank Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Birth Squat | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Aerobic Conditioning: Jog/Walk 20 minutes

--- WEEKS 7–12 ---

WORKOUT 1 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
Hinge Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Overhead Push/Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Carry Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal Push Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal/Vertical Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Squat Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Plank Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Birth Squat | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Aerobic Conditioning: Continuous Step Ups — 4 minutes on / 1 minute off x5

WORKOUT 2 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
Squat Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Overhead Push/Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Carry Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal Push Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal/Vertical Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Hinge Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Plank Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Birth Squat | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Aerobic Conditioning: Jog/Walk 20 minutes

WORKOUT 3 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
Squat Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Overhead Push/Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Carry Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal Push Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Horizontal/Vertical Pull Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Hinge Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Plank Variation | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Birth Squat | 3 x 45 sec | 4 x 45 sec | 3 x 45 sec
Aerobic Conditioning: Jog/Walk 20 minutes
`

const GUIDE_T3 = `
THIRD TRIMESTER PROGRAM — 12 weeks total (3 rotating workouts)

IMPORTANT NOTES:
- Avoid prone position (belly-down) AND supine position (flat on back) exercises.
- Use supported, seated, or side-lying alternatives.
- Exercises use specific names (not "variations") — use exactly the named exercises.
- Sets x Reps format (not timed). Weeks 5–6 and 11–12 are deload weeks with reduced volume.

WARM UP (perform before every workout):
Off Feet Cardio | 5 minutes
Lateral Band Walks | 10 yards each leg
In Place Band March | 10 steps each leg
Supported Split Squat | 10 each leg
Supported Squat | 10 reps
Band Pull Apart | 10 reps
Wall Slide | 10 reps

COOL DOWN — Repeat 2x:
Wide Child's Pose w/ Side Stretch (wide knees, sit back as far as comfortable, reach side to side for 5 breaths rotating, 2–3 minutes)
Deep Breathing & Relaxation (calmly execute deep breathing and progressive muscle relaxation, 2–3 minutes)

--- WEEKS 1–6 ---

WORKOUT 1 (Weeks 1–6):
Movement | Weeks 1–2 | Weeks 3–4 | Weeks 5–6
Squat Variation | 4 x 8 | 4 x 10 | 3 x 5
Incline Push Up | 3 x 10 | 3 x 10 | 2 x 8
Band Pull Apart | 3 x 12 | 3 x 12 | 2 x 10
Seated DB Overhead Press | 2 x 10 | 2 x 10 | 2 x 5
Hip Thrust | 2 x 15 | 2 x 15 | 2 x 8
Aerobic Conditioning: Cycling 15 minutes

WORKOUT 2 (Weeks 1–6):
Movement | Weeks 1–2 | Weeks 3–4 | Weeks 5–6
Step Ups | 4 x 8 each side | 4 x 10 each side | 3 x 5 each side
1 Arm DB Row | 3 x 10 each side | 3 x 10 each side | 2 x 8 each side
KB Deadlift | 3 x 12 | 3 x 12 | 2 x 10
Side Lying Leg Lift | 2 x 10 | 2 x 10 | 2 x 5
DB Curl to Press | 2 x 15 | 2 x 15 | 2 x 8
Aerobic Conditioning: Continuous Step Ups 15 minutes

WORKOUT 3 (Weeks 1–6):
Movement | Weeks 1–2 | Weeks 3–4 | Weeks 5–6
Supported Split Squat | 4 x 8 each side | 4 x 10 each side | 3 x 5 each side
Incline DB Press | 3 x 10 | 3 x 10 | 2 x 8
Seated Band Pulldown | 3 x 12 | 3 x 12 | 2 x 10
Lateral Raises | 2 x 10 | 2 x 10 | 2 x 5
Band Monster Walk | 2 x 15 each side | 2 x 15 each side | 2 x 8 each side
Aerobic Conditioning: Sled Drag 15 minutes

--- WEEKS 7–12 (same exercises, same progression as weeks 1–6) ---

WORKOUT 1 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
Squat Variation | 4 x 8 | 4 x 10 | 3 x 5
Incline Push Up | 3 x 10 | 3 x 10 | 2 x 8
Band Pull Apart | 3 x 12 | 3 x 12 | 2 x 10
Seated DB Overhead Press | 2 x 10 | 2 x 10 | 2 x 5
Hip Thrust | 2 x 15 | 2 x 15 | 2 x 8
Aerobic Conditioning: Cycling 15 minutes

WORKOUT 2 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
Step Ups | 4 x 8 each side | 4 x 10 each side | 3 x 5 each side
1 Arm DB Row | 3 x 10 each side | 3 x 10 each side | 2 x 8 each side
KB Deadlift | 3 x 12 | 3 x 12 | 2 x 10
Side Lying Leg Lift | 2 x 10 | 2 x 10 | 2 x 5
DB Curl to Press | 2 x 15 | 2 x 15 | 2 x 8
Aerobic Conditioning: Continuous Step Ups 15 minutes

WORKOUT 3 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
Supported Split Squat | 4 x 8 each side | 4 x 10 each side | 3 x 5 each side
Incline DB Press | 3 x 10 | 3 x 10 | 2 x 8
Seated Band Pulldown | 3 x 12 | 3 x 12 | 2 x 10
Lateral Raises | 2 x 10 | 2 x 10 | 2 x 5
Band Monster Walk | 2 x 15 each side | 2 x 15 each side | 2 x 8 each side
Aerobic Conditioning: Sled Drag 15 minutes
`

const GUIDE_PP_STANDARD = `
POSTPARTUM – STANDARD DELIVERY PROGRAM — 18 weeks total

PROGRAM OVERVIEW (Phases):
Weeks 0–2:   Rest/Breathwork — Rest/recovery. Gentle pelvic floor contraction/relaxation with breathwork (2 days/week up to every day)
Weeks 3–4:   Rest/Breathwork/Walk — Breathwork + pelvic floor work. Begin walking around the home.
Weeks 5–6:   Movement Patterns — Total body mobility, bodyweight movement patterns, pelvic and core control (start 2x/week then 3x/week)
Weeks 7–8:   Integration — Total body strength with focus on single leg hip stability, pelvic and core control
Weeks 9–12:  Low Level Plyometrics — Building on previous weeks, adding lower level plyometrics to prepare for running
Weeks 13–18: Return to Running — Continuing strength, slowly introducing running as R2R screen allows

*Please consult and be cleared by your medical provider before starting this program. Seek clearance if adverse reactions or setbacks occur.*

WARM UP (starts no earlier than week 5; perform before every workout):
Cardio | 5 minutes
Single Leg Balance | 30 seconds each leg
3 Second Box Breathing (3 in / 3 hold / 3 out) | 10 reps
Plank | 30 seconds
Glute Bridge | 15 reps
Lunge in Place | 5 each leg
World's Greatest Stretch | 5 reps
Push Up | 10 reps

COOL DOWN (starts no earlier than week 5):
Walking | 5 minutes
Quad Pull | 5 each leg
Straight Leg March | 5 each leg
Lunge w/ Overhead Reach | 5 each leg
Hamstring Scoop | 5 each leg
Lateral Lunge | 5 each leg
Hip Cradle (if possible) | 5 each leg
Child Pose | 30 seconds

--- WEEKS 1–6 (2 workouts; alternate days) ---

WORKOUT 1 (Weeks 1–6):
Movement | Weeks 1–2 | Weeks 3–4 | Weeks 5–6
Box Breathing | 1 x 1 min | 2 x 1 min | 3 x 1 min
Cat Cow | 1 x 8 | 2 x 8 | 3 x 8
Single Leg QL Stretch | 1 x 30 sec | 2 x 30 sec | 3 x 30 sec
Walking | 1 x 5 min | 2 x 5 min | 3 x 4 min
Pelvic Tilts | 1 x 10 | 2 x 8 | 3 x 8
Glute Bridge | 1 x 10 | 2 x 8 | 3 x 8
Heel Slides | 1 x 10 | 2 x 8 | 3 x 8
Aerobic Conditioning: Weeks 1–2: Walking 5 min | Weeks 3–4: Walking 10 min | Weeks 5–6: Walking 12 min

WORKOUT 2 (Weeks 1–6) — same as Workout 1:
Box Breathing | 1 x 1 min | 2 x 1 min | 3 x 1 min
Cat Cow | 1 x 8 | 2 x 8 | 3 x 8
Single Leg QL Stretch | 1 x 30 sec | 2 x 30 sec | 3 x 30 sec
Walking | 1 x 5 min | 2 x 5 min | 3 x 4 min
Pelvic Tilts | 1 x 10 | 2 x 8 | 3 x 8
Glute Bridge | 1 x 10 | 2 x 8 | 3 x 8
Heel Slides | 1 x 10 | 2 x 8 | 3 x 8
Aerobic Conditioning: Weeks 1–2: Walking 5 min | Weeks 3–4: Walking 10 min | Weeks 5–6: Walking 12 min

--- WEEKS 7–12 (3 workouts; rotate through) ---

WORKOUT 1 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
Cat Cow | 2 x 10 | 3 x 10 | 3 x 12
High Knee March in Place | 2 x 10 each side | 3 x 10 each side | 3 x 15 each side
Arm Circles | 2 x 12 | 3 x 10 | 3 x 12
Bodyweight Squat | 2 x 10 | 3 x 10 | 3 x 12
Incline Push Ups | 2 x 10 | 3 x 10 | 3 x 12
Seated Band Row | 2 x 10 | 3 x 12 | 3 x 15
Aerobic Conditioning: Weeks 7–8: Walking 10 min | Weeks 9–10: Walking 15 min | Weeks 11–12: Walking 20 min

WORKOUT 2 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
Pelvic Tilts | 2 x 8 | 2 x 10 | 3 x 10
Side Lying QL Stretch | 2 x 30 sec | 2 x 30 sec | 3 x 30 sec
Split Stance Open Book | 2 x 10 each side | 2 x 10 each side | 3 x 10 each side
Step Ups | 2 x 8 each side | 2 x 12 each side | 3 x 10 each side
Bodyweight Good Mornings | 2 x 8 | 2 x 12 | 3 x 10
Side Lying Clamshells | 2 x 8 each side | 2 x 12 each side | 3 x 10 each side
Aerobic Conditioning: Weeks 7–8: Walking 10 min | Weeks 9–10: Walking 12 min | Weeks 11–12: Walking 15 min

WORKOUT 3 (Weeks 7–12):
Movement | Weeks 7–8 | Weeks 9–10 | Weeks 11–12
High Knee March in Place | 2 x 30 sec | 2 x 30 sec | 3 x 30 sec
Quadruped Hip Circles | 2 x 10 each side | 3 x 10 each side | 3 x 10 each side
Deadbug Hold | 2 x 5 breaths | 2 x 5 breaths | 3 x 5 breaths
Split Squat | 2 x 8 each side | 2 x 10 each side | 2 x 12 each side
DB Overhead Press | 2 x 8 | 2 x 10 | 2 x 12
Glute Bridge w/ 3 second hold each rep | 2 x 8 | 2 x 10 | 2 x 12
Aerobic Conditioning: Weeks 7–8: Cardio Machine of Choice 10 min | Weeks 9–10: Cardio Machine 10 min | Weeks 11–12: Cardio Machine 15 min

--- WEEKS 13–18 (3 workouts; rotate through) ---

WORKOUT 1 (Weeks 13–18):
Movement | Weeks 13–15 | Weeks 16–18
Quadruped Hip Circles | 2 x 10 | 2 x 10
High Knee March | 2 x 20 yd | 2 x 20 yd
Side Lying Open Books | 2 x 10 | 2 x 10
Front Squat Variation | 4 x 5 | 4 x 5
Push Ups | 2 x 10 | 2 x 10
1 Arm DB Row | 2 x 12 each side | 2 x 12 each side
Aerobic Conditioning: Weeks 15–18: Incline Treadmill Walk 15 min

WORKOUT 2 (Weeks 13–18):
Movement | Weeks 13–15 | Weeks 16–18
Goblet High Knee March | 2 x 40 steps | 2 x 40 steps
Standing Hurdle Walk Over | 2 x 10 each side | 2 x 10 each side
Dead Hang from Pull Up Bar | 2 x 15 sec | 2 x 15 sec
DB Split Squat | 4 x 6 each side | 4 x 6 each side
Seated DB Overhead Press | 3 x 8 | 3 x 8
Glute Bridge (Single Leg if able) | 3 x 10 | 3 x 10
Aerobic Conditioning: Weeks 15–18: Incline Treadmill Walk 15 min

WORKOUT 3 (Weeks 13–18):
Movement | Weeks 13–15 | Weeks 16–18
Pelvic Tilts | 2 x 10 | 2 x 10
Bird Dog | 2 x 8 each side | 2 x 8 each side
Backwards Sled Drag | 2 x 20 yd | 2 x 20 yd
Goblet Step Ups | 4 x 5 each side | 4 x 5 each side
Tall Plank | 2 x 5 breaths | 2 x 5 breaths
DB RDL | 2 x 12 | 2 x 12
Aerobic Conditioning: Weeks 15–18: Cycling 15 min
`

const GUIDE_PP_CSECTION = `
POSTPARTUM – C-SECTION DELIVERY PROGRAM — 18 weeks total

PROGRAM OVERVIEW (same phases as standard delivery, but initial phase is extended to 8 weeks):
Weeks 0–2:   Rest/Breathwork — Rest/recovery. Gentle pelvic floor contraction/relaxation with breathwork
Weeks 3–4:   Rest/Breathwork/Walk — Breathwork + pelvic floor work. Begin walking around the home.
Weeks 5–8:   Movement Patterns — Extended initial phase (4 weeks vs 2 for standard). Total body mobility, bodyweight movement patterns, pelvic and core control
Weeks 9–12:  Integration into strength — Total body strength with single leg hip stability, pelvic and core control
Weeks 13–14: Low level plyometrics — Adding lower level plyometrics to prepare for running
Weeks 15–18: Return to activity — Continuing strength and cardio progression

*C-section delivery requires a more conservative return to exercise. Please consult and be cleared by your medical provider before starting. Seek clearance if adverse reactions or setbacks occur.*

WARM UP (starts no earlier than week 5; perform before every workout):
Cardio | 5 minutes
Single Leg Balance | 30 seconds each leg
3 Second Box Breathing (3 in / 3 hold / 3 out) | 10 reps
Plank | 30 seconds
Glute Bridge | 15 reps
Lunge in Place | 5 each leg
World's Greatest Stretch | 5 reps
Push Up | 10 reps

COOL DOWN (starts no earlier than week 5):
Walking | 5 minutes
Quad Pull | 5 each leg
Straight Leg March | 5 each leg
Lunge w/ Overhead Reach | 5 each leg
Hamstring Scoop | 5 each leg
Lateral Lunge | 5 each leg
Hip Cradle (if possible) | 5 each leg
Child Pose | 30 seconds

--- WEEKS 1–8 (2 workouts; alternate days — extended initial phase) ---

WORKOUT 1 (Weeks 1–8):
Movement | Weeks 1–2 | Weeks 3–4 | Weeks 5–8
Box Breathing | 1 x 1 min | 2 x 1 min | 3 x 1 min
Cat Cow | 1 x 8 | 2 x 8 | 3 x 8
Single Leg QL Stretch | 1 x 30 sec | 2 x 30 sec | 3 x 30 sec
Walking | 1 x 5 min | 2 x 5 min | 3 x 4 min
Pelvic Tilts | 1 x 10 | 2 x 8 | 3 x 8
Glute Bridge | 1 x 10 | 2 x 8 | 3 x 8
Heel Slides | 1 x 10 | 2 x 8 | 3 x 8
Aerobic Conditioning: Weeks 1–2: Walking 5 min | Weeks 3–4: Walking 10 min | Weeks 5–8: Walking 12 min

WORKOUT 2 (Weeks 1–8) — same as Workout 1:
Box Breathing | 1 x 1 min | 2 x 1 min | 3 x 1 min
Cat Cow | 1 x 8 | 2 x 8 | 3 x 8
Single Leg QL Stretch | 1 x 30 sec | 2 x 30 sec | 3 x 30 sec
Walking | 1 x 5 min | 2 x 5 min | 3 x 4 min
Pelvic Tilts | 1 x 10 | 2 x 8 | 3 x 8
Glute Bridge | 1 x 10 | 2 x 8 | 3 x 8
Heel Slides | 1 x 10 | 2 x 8 | 3 x 8
Aerobic Conditioning: Weeks 1–2: Walking 5 min | Weeks 3–4: Walking 10 min | Weeks 5–8: Walking 12 min

--- WEEKS 9–14 (3 workouts; rotate through) ---

WORKOUT 1 (Weeks 9–14):
Movement | Weeks 9–10 | Weeks 11–12 | Weeks 13–14
Cat Cow | 2 x 10 | 3 x 10 | 3 x 12
High Knee March in Place | 2 x 10 each side | 3 x 10 each side | 3 x 15 each side
Arm Circles | 2 x 12 | 3 x 10 | 3 x 12
Bodyweight Squat | 2 x 10 | 3 x 10 | 3 x 12
Incline Push Ups | 2 x 10 | 3 x 10 | 3 x 12
Seated Band Row | 2 x 10 | 3 x 12 | 3 x 15
Aerobic Conditioning: Weeks 9–10: Walking 10 min | Weeks 11–12: Walking 15 min | Weeks 13–14: Walking 20 min

WORKOUT 2 (Weeks 9–14):
Movement | Weeks 9–10 | Weeks 11–12 | Weeks 13–14
Pelvic Tilts | 2 x 8 | 2 x 10 | 3 x 10
Side Lying QL Stretch | 2 x 30 sec | 2 x 30 sec | 3 x 30 sec
Split Stance Open Book | 2 x 10 each side | 2 x 10 each side | 3 x 10 each side
Step Ups | 2 x 8 each side | 2 x 12 each side | 3 x 10 each side
KB/DB Deadlift | 2 x 8 | 2 x 12 | 3 x 10
Side Lying Clamshells | 2 x 8 each side | 2 x 12 each side | 3 x 10 each side
Aerobic Conditioning: Weeks 9–10: Walking 10 min | Weeks 11–12: Walking 12 min | Weeks 13–14: Walking 15 min

WORKOUT 3 (Weeks 9–14):
Movement | Weeks 9–10 | Weeks 11–12 | Weeks 13–14
High Knee March in Place | 2 x 30 sec | 2 x 30 sec | 3 x 30 sec
Quadruped Hip Circles | 2 x 10 each side | 3 x 10 each side | 3 x 10 each side
Deadbug Hold | 2 x 5 breaths | 2 x 5 breaths | 3 x 5 breaths
Split Squat | 2 x 8 each side | 2 x 10 each side | 2 x 12 each side
DB Overhead Press | 2 x 8 | 2 x 10 | 2 x 12
Glute Bridge w/ 3 second hold each rep | 2 x 8 | 2 x 10 | 2 x 12
Aerobic Conditioning: Weeks 9–10: Cardio Machine of Choice 10 min | Weeks 11–12: Cardio Machine 10 min | Weeks 13–14: Cardio Machine 15 min

--- WEEKS 15–18 (3 workouts; rotate through) ---

WORKOUT 1 (Weeks 15–18):
Movement | Weeks 15–16 | Weeks 17–18
Quadruped Hip Circles | 2 x 10 | 2 x 10
High Knee March | 2 x 20 yd | 2 x 20 yd
Side Lying Open Books | 2 x 10 | 2 x 10
Front Squat Variation | 4 x 5 | 4 x 5
Push Ups | 2 x 10 | 2 x 10
1 Arm DB Row | 2 x 12 each side | 2 x 12 each side
Aerobic Conditioning: Weeks 15–18: Incline Treadmill Walk 15 min

WORKOUT 2 (Weeks 15–18):
Movement | Weeks 15–16 | Weeks 17–18
Pelvic Tilts | 2 x 10 | 2 x 10
Bird Dog | 2 x 8 each side | 2 x 8 each side
Backwards Sled Drag | 2 x 20 yd | 2 x 20 yd
Goblet Step Ups | 4 x 5 each side | 4 x 5 each side
Tall Plank | 2 x 5 breaths | 2 x 5 breaths
DB RDL | 2 x 12 | 2 x 12
Aerobic Conditioning: Weeks 15–18: Cycling 15 min

WORKOUT 3 (Weeks 15–18):
Movement | Weeks 15–16 | Weeks 17–18
Goblet High Knee March | 2 x 40 steps | 2 x 40 steps
Standing Hurdle Walk Over | 2 x 10 each side | 2 x 10 each side
Dead Hang from Pull Up Bar | 2 x 15 sec | 2 x 15 sec
DB Split Squat | 4 x 6 each side | 4 x 6 each side
Seated DB Overhead Press | 3 x 8 | 3 x 8
Glute Bridge (Single Leg if able) | 3 x 10 | 3 x 10
Aerobic Conditioning: Weeks 15–18: Incline Treadmill Walk 15 min
`

// ---------------------------------------------------------------------------

const GUIDE_TABLES: Record<PregnancyPhase, string> = {
  't1':                  GUIDE_T1,
  't2':                  GUIDE_T2,
  't3':                  GUIDE_T3,
  'postpartum-standard': GUIDE_PP_STANDARD,
  'postpartum-csection': GUIDE_PP_CSECTION,
}

const TOTAL_WEEKS: Record<PregnancyPhase, number> = {
  't1':                  12,
  't2':                  12,
  't3':                  12,
  'postpartum-standard': 18,
  'postpartum-csection': 18,
}

// Guide-specified training frequency per phase/week-range.
// These are non-negotiable — user's daysPerWeek preference does not apply to pregnancy plans.
const PHASE_WORKOUT_SCHEDULE: Record<PregnancyPhase, string> = {
  't1':
    'Phase 1 (Weeks 1–6):  3 training days/week — rotate Workout 1 → 2 → 3.\n' +
    '  sets_by_block for each exercise: [Wks 1–2 sets, Wks 3–4 sets, Wks 5–6 sets] (e.g. [3, 4, 5] for T1).\n' +
    '  reps_by_block: omit if the duration/reps column is the same across all three sub-ranges (T1 exercises are 30 sec throughout).\n' +
    'Phase 2 (Weeks 7–12): 3 training days/week — rotate Workout 1 → 2 → 3.\n' +
    '  sets_by_block: [Wks 7–8 sets, Wks 9–10 sets, Wks 11–12 sets].',
  't2':
    'Phase 1 (Weeks 1–6):  3 training days/week — rotate Workout 1 → 2 → 3.\n' +
    '  sets_by_block for each exercise: [Wks 1–2 sets, Wks 3–4 sets, Wks 5–6 sets] (e.g. [3, 4, 3] for T2 deload).\n' +
    '  reps_by_block: omit if the duration/reps column is the same across all three sub-ranges (T2 exercises are 45 sec throughout).\n' +
    'Phase 2 (Weeks 7–12): 3 training days/week — rotate Workout 1 → 2 → 3.\n' +
    '  sets_by_block: [Wks 7–8 sets, Wks 9–10 sets, Wks 11–12 sets] (e.g. [3, 4, 3]).',
  't3':
    'Phase 1 (Weeks 1–6):  3 training days/week — rotate Workout 1 → 2 → 3.\n' +
    '  sets_by_block for each exercise: [Wks 1–2 sets, Wks 3–4 sets, Wks 5–6 sets] (e.g. [4, 4, 3]).\n' +
    '  reps_by_block: REQUIRED when reps differ across sub-ranges (e.g. [8, 10, 5] for Squat Variation).\n' +
    'Phase 2 (Weeks 7–12): 3 training days/week — rotate Workout 1 → 2 → 3.\n' +
    '  sets_by_block: [Wks 7–8 sets, Wks 9–10 sets, Wks 11–12 sets].\n' +
    '  reps_by_block: REQUIRED when reps differ (same progression pattern as Phase 1).',
  'postpartum-standard':
    'Phase 1 (Weeks 1–6):  2 training days/week — Workout 1 on day 1 (Mon), Workout 2 on day 4 (Thu).\n' +
    '  sets_by_block: [Wks 1–2 sets, Wks 3–4 sets, Wks 5–6 sets] (e.g. [1, 2, 3]).\n' +
    '  reps_by_block: include if reps differ across sub-ranges (e.g. Walking: [5, 5, 4] min).\n' +
    'Phase 2 (Weeks 7–18): 3 training days/week — rotate Workout 1 → 2 → 3.\n' +
    '  Weeks 7–12 and 13–18 have separate exercise blocks in the guide; combine both blocks into one phase2 template.\n' +
    '  sets_by_block: one entry per 2-week block → [Wks 7–8, Wks 9–10, Wks 11–12, Wks 13–15, Wks 16–18] = 5 entries.\n' +
    '  reps_by_block: include when reps differ across those blocks.',
  'postpartum-csection':
    'Phase 1 (Weeks 1–8):  2 training days/week — Workout 1 on day 1 (Mon), Workout 2 on day 4 (Thu).\n' +
    '  sets_by_block: [Wks 1–2, Wks 3–4, Wks 5–6, Wks 7–8] = 4 entries.\n' +
    '  reps_by_block: include if reps differ across sub-ranges.\n' +
    'Phase 2 (Weeks 9–18): 3 training days/week — rotate Workout 1 → 2 → 3.\n' +
    '  Weeks 9–14 and 15–18 have separate exercise blocks in the guide; combine both blocks into one phase2 template.\n' +
    '  sets_by_block: [Wks 9–10, Wks 11–12, Wks 13–14, Wks 15–16, Wks 17–18] = 5 entries.\n' +
    '  reps_by_block: include when reps differ across those blocks.',
}

export function buildPregnancyPrompt(params: PregnancyPromptParams): string {
  const { phase, equipment } = params
  const injuries   = params.injuries?.trim() || ''
  const phaseLabel = PHASE_LABELS[phase]
  const guideTable = GUIDE_TABLES[phase]
  const totalWeeks = TOTAL_WEEKS[phase]
  const schedule   = PHASE_WORKOUT_SCHEDULE[phase]

  const lines: string[] = [
    'You are a perinatal fitness specialist generating a conservative, safe training plan following official USAF pregnancy and postpartum physical fitness guidance.',
    'CRITICAL: This plan is for a pregnant or postpartum individual. Safety is the absolute top priority. You must follow the guide below exactly and must never add intensity, volume, or exercises beyond what is explicitly listed.',
    'CRITICAL: Do NOT draw on general military fitness, PFRA test standards, PT test prep, or the Warfighter\'s Fitness Playbook for any part of this plan. This is a perinatal health plan only.',
    '',
    `Phase: ${phaseLabel}`,
    `Total weeks: ${totalWeeks} (FIXED — determined by the official guide; not affected by test date or any user input)`,
    '',
    '## Official USAF Guide for This Phase',
    guideTable,
    '',
    '## Guide-Specified Training Schedule (follow exactly)',
    schedule,
    '',
    '## User Context',
  ]

  if (injuries) lines.push(`- Injuries or limitations: ${injuries}`)
  lines.push(`- Equipment available: ${equipment === 'gym' ? 'Gym equipment and bodyweight' : 'Bodyweight only'}`)

  lines.push(
    '',
    '## Rules',
    `YOU MUST SET total_weeks TO EXACTLY ${totalWeeks}. This is non-negotiable.`,
    'YOU MUST ONLY USE EXERCISES AND PROGRESSIONS EXACTLY AS SHOWN IN THE GUIDE TABLE ABOVE. Do not add, remove, rename, or change any exercise, set count, rep count, or duration.',
    'NEVER add intensity or volume beyond what the guide prescribes. These plans are intentionally conservative for pregnancy/postpartum health — do not enhance, intensify, or supplement them.',
    'DO NOT reference PFRA scores, PT test standards, run times, push-up/sit-up standards, or the Warfighter\'s Fitness Playbook anywhere in the JSON output. This is a perinatal health plan.',
    'For "Variation" exercises: use the examples from the guide notes as your reference (e.g. Squat Variation → Air Squat or Goblet Squat). NEVER choose high-impact, loaded-barbell, or plyometric variants. Always choose the most conservative, pregnancy-appropriate option.',
    'YOU MUST USE EXACTLY THE TRAINING FREQUENCY FROM THE GUIDE SCHEDULE ABOVE for each phase. Do not use a different number of training days.',
    'NEVER SCHEDULE MORE THAN 2 TRAINING DAYS IN A ROW. After 2 consecutive training days, there must be at least one rest day before the next training day.',
    'For 3 training days/week: schedule on days 1, 3, 5 (Mon, Wed, Fri). Days 2, 4, 6, 7 are rest days.',
    'For 2 training days/week: schedule on days 1, 4 (Mon, Thu). Days 2, 3, 5, 6, 7 are rest days.',
    'REST DAYS must have is_rest: true, label "Rest – Optional light walking 10–20 minutes", focus "Recovery", and empty warmup/exercises/cooldown arrays.',
    'EVERY TRAINING DAY MUST INCLUDE THE COMPLETE WARM UP AND COOL DOWN from the guide above in the warmup[] and cooldown[] arrays. Include every listed item — do not omit or abbreviate.',
    'Assign one named Workout per training day (Workout 1, Workout 2, Workout 3…), rotating in order.',
    'Use phase_templates with one phase per guide week-range block.',
    'For "Variation" exercises (e.g. Squat Variation): choose one specific appropriate exercise and use it consistently throughout.',
    '',
    'CRITICAL — sets_by_block and reps_by_block rules:',
    '  Every exercise MUST include sets_by_block array — one entry per 2-week sub-range within the phase (see Guide-Specified Training Schedule above).',
    '  Example for T2 Phase 1: sets_by_block: [3, 4, 3]  (Wks 1–2=3sets, Wks 3–4=4sets, Wks 5–6=3sets deload)',
    '  Example for T1 Phase 1: sets_by_block: [3, 4, 5]  (Wks 1–2=3, Wks 3–4=4, Wks 5–6=5)',
    '  Example for T3 Phase 1 Squat Variation: sets_by_block: [4, 4, 3]',
    '  Include reps_by_block only when the rep count differs across sub-ranges (e.g. T3 Squat Variation Wks 1-2=8, 3-4=10, 5-6=5 → reps_by_block: [8, 10, 5]).',
    '  For T1/T2 timed exercises where only sets change (duration stays constant), omit reps_by_block.',
    '  base_sets MUST equal sets_by_block[0] (first block). base_reps MUST equal reps_by_block[0] if present, else the constant value.',
    '',
    'Exercise encoding rules — apply exactly:',
    '  "N x M sec" (e.g. "3 x 30 sec") → base_sets=N, base_reps=M, reps_unit="seconds"',
    '  "N x M" plain reps (e.g. "4 x 8", "3 x 10") → base_sets=N, base_reps=M, reps_unit="reps"',
    '  "N x M breaths" (e.g. "2 x 5 breaths") → base_sets=N, base_reps=M, reps_unit="breaths"',
    '  "N x M yd" or "N x M yards" (e.g. "2 x 20 yd") → base_sets=N, base_reps=M, reps_unit="yards"',
    '  "N x M steps" (e.g. "2 x 40 steps") → base_sets=N, base_reps=M, reps_unit="steps"',
    '  "N x M min" (e.g. "2 x 1 min", "3 x 4 min") → base_sets=N, base_reps=M, reps_unit="minutes"',
    '  Aerobic — simple duration (e.g. "Jog 30 minutes", "Walking 10 min", "Cycling 15 minutes") → base_sets=1, base_reps=<the number>, reps_unit="minutes"',
    '  Aerobic — total-time intervals (e.g. "60 sec on / 120 sec off for 15 minutes") → base_sets=1, base_reps=15, reps_unit="minutes", notes="Intervals: 60 sec on / 120 sec off"',
    '  Aerobic — repeat intervals (e.g. "Jog 3 min on / 2 min off x8") → base_sets=8, base_reps=3, reps_unit="minutes", notes="2 min rest between intervals"',
    '  Aerobic — sets × on/off (e.g. "4 minutes on / 1 minute off x5") → base_sets=5, base_reps=4, reps_unit="minutes", notes="1 min rest between sets"',
    '  Aerobic — week-range variant (e.g. "Weeks 7–8: Walking 10 min | Weeks 9–10: Walking 15 min | Weeks 11–12: Walking 20 min") → base_sets=1, base_reps=<first range value>, reps_unit="minutes", reps_by_block=[first, second, third] values.',
    'NEVER set reps_unit="reps" for any exercise measured in time. Any exercise description containing "minutes", "min", "seconds", or "sec" is time-based — use the matching time unit.',
    'Warmup items: set reps_display to the text after the "|" in the guide exactly (e.g. "5 minutes", "30 seconds each leg", "15 reps").',
    'Cooldown items: set hold to the text after the "|" in the guide exactly (e.g. "5 each leg", "30 seconds").',
    'For per-side exercises (e.g. "3 x 10 each side"): note "each side" in the exercise notes field.',
  )

  if (injuries) {
    lines.push(`YOU MUST MODIFY OR SUBSTITUTE any exercise that could aggravate: ${injuries}. Record the substitution in the exercise notes field.`)
  }
  if (equipment === 'bodyweight') {
    lines.push('YOU MUST USE BODYWEIGHT-ONLY exercises where possible. Substitute equipment exercises (e.g. DB Row, Sled Drag) with a suitable bodyweight alternative and note the substitution in the exercise notes field.')
  }

  lines.push(
    '',
    '## ENCODING REMINDER — apply this to every single exercise before writing its JSON:',
    '  "N x M sec" or "N x M seconds" in the guide → reps_unit MUST be "seconds" (e.g. "3 x 45 sec" → base_sets=3, base_reps=45, reps_unit="seconds", sets_by_block=[3,4,3])',
    '  "N x M" with no unit → reps_unit MUST be "reps" (e.g. "4 x 8" → base_sets=4, base_reps=8, reps_unit="reps", sets_by_block=[4,4,3], reps_by_block=[8,10,5])',
    '  Any duration in minutes → reps_unit MUST be "minutes" (e.g. "Jog 30 minutes" → base_sets=1, base_reps=30, reps_unit="minutes")',
    '  reps_unit="reps" is ONLY correct when the guide shows a plain count with no time unit.',
    '  sets_by_block is REQUIRED for every exercise — encode all sub-range columns, not just the first.',
    '',
    '## Output Format',
    'Respond with ONLY a valid JSON object. No markdown fences, no explanation, no text outside the JSON.',
    '',
    'Use phase_templates to represent each guide week-range block (the system normalizes phase1 → week_template, phase2 → week_template_phase2).',
    'For postpartum phases with 3 guide week-ranges: phase1 = early recovery range (2 training days/week); phase2 = the remaining weeks combined (3 training days/week).',
    '',
    'Required schema:',
    '{',
    `  "plan_name": "USAF ${phaseLabel} Training Plan",`,
    `  "total_weeks": ${totalWeeks},`,
    '  "phases": [',
    '    { "name": "Phase 1", "start_week": 1, "end_week": <last week of first range>, "focus": "<phase focus description>" },',
    '    { "name": "Phase 2", "start_week": <first week of second range>, "end_week": <last week>, "focus": "<phase focus description>" }',
    '  ],',
    '  "phase_templates": {',
    '    "phase1": {',
    '      "week_template": [ <7 day objects, day_of_week 1–7> ]',
    '    },',
    '    "phase2": {',
    '      "week_template": [ <7 day objects, day_of_week 1–7> ]',
    '    }',
    '  }',
    '}',
    '',
    'Day object structure:',
    '{',
    '  "day_of_week": number (1=Mon … 7=Sun),',
    '  "label": "Workout N" or "Rest – Optional light walking 10–20 minutes",',
    '  "focus": "string",',
    '  "is_rest": boolean,',
    '  "warmup": [{ "name": "string", "reps_display": "string" }],',
    '  "exercises": [{ "name": "string", "base_sets": number, "sets_by_block": [number,...], "base_reps": number, "reps_by_block": [number,...] (omit if constant), "reps_unit": "string", "notes": "string|null" }],',
    '  "cooldown": [{ "name": "string", "hold": "string" }]',
    '}',
    '',
    'week_template must contain exactly 7 entries (day_of_week 1–7). Training days: warmup/exercises/cooldown fully populated. Rest days: is_rest true, all arrays empty.',
    'Include aerobic conditioning as the last exercise in the exercises array on every training day.',
  )

  return lines.join('\n')
}
